import { SearchProgressStore } from "./searchProgressStore";
import { CardKind } from "./types";

// Dev-only incremental feeder exposed as window.__searchProgress (wired in
// PusherBootstrap, guarded by NODE_ENV). Simulates the Pusher stream by emitting
// ONE event at a time on a timer — so the UI shows "one message at a time,
// previous stays until the next". Real BE/middleware events drive the identical
// SearchProgressStore.apply path. The canned hotel cards carry the FULL content
// incl. a placeholder image URL, mirroring how the BE will stream every field.
interface FeedStep {
  delay: number; // ms from demo() start
  run: () => void;
}

const HOTELS = [
  { name: "Park Hyatt Vienna", rating: "9.6", price: "$420" },
  { name: "InterContinental Vienna", rating: "9.2", price: "$310" },
  { name: "Grand Hotel Wien", rating: "9.4", price: "$385" },
  { name: "Anantara Palais Hansen", rating: "9.1", price: "$365" },
];

const SCRIPT: FeedStep[] = [
  { delay: 0, run: () => SearchProgressStore.apply({ type: "start", header: "Searching Vienna hotels" }) },
  { delay: 300, run: () => SearchProgressStore.apply({ type: "message", text: "Handing off to our hotel agent…" }) },
  { delay: 1300, run: () => SearchProgressStore.apply({ type: "message", text: "Scouting 5-star stays in Vienna for Wednesday…" }) },
  { delay: 2500, run: () => SearchProgressStore.apply({ type: "message", text: "Considering 14 of 1,165 hotels…" }) },
  ...HOTELS.map((h, i) => ({
    delay: 3200 + i * 700,
    run: () =>
      SearchProgressStore.apply({
        type: "card" as const,
        card: {
          kind: "hotel" as CardKind,
          id: `h${i}`,
          data: { ...h, imageUrl: `https://picsum.photos/seed/zenvoya-hotel-${i + 1}/120/120` },
        },
      }),
  })),
  { delay: 6300, run: () => SearchProgressStore.apply({ type: "message", text: "Comparing the final shortlist for the best value…" }) },
  { delay: 7400, run: () => SearchProgressStore.apply({ type: "end" }) },
];

// Multi-city demo matching the DEPLOYED api-dev backend. Two stages:
//  1. PINPOINTING — one resolve_location spinner PER CITY, concurrent + resolving
//     independently (the real N-concurrent-spinners moment).
//  2. HOTEL SEARCH — ONE search_hotels spinner labelled with all cities; per-city
//     narration lines + every city's cards grouped under it (all share one stepId).
//     NOT one hotel spinner per city — group-by-stepId + append handles this.
const LONDON = [
  { name: "The Savoy", rating: "9.4", price: "$520" },
  { name: "Claridge's", rating: "9.6", price: "$610" },
];
const PARIS = [
  { name: "Ritz Paris", rating: "9.5", price: "$890" },
  { name: "Le Meurice", rating: "9.3", price: "$720" },
];
const hotelCard = (
  stepId: string,
  i: number,
  h: { name: string; rating: string; price: string }
): void =>
  SearchProgressStore.apply({
    type: "card",
    stepId,
    card: {
      kind: "hotel" as CardKind,
      id: `${stepId}-${i}`,
      data: { ...h, imageUrl: `https://picsum.photos/seed/zenvoya-${stepId}-${i}/120/120` },
    },
  });

const MULTI_SCRIPT: FeedStep[] = [
  { delay: 0, run: () => SearchProgressStore.apply({ type: "start" }) },
  { delay: 250, run: () => SearchProgressStore.apply({ type: "message", text: "On it — searching London & Paris…" }) },
  { delay: 700, run: () => SearchProgressStore.apply({ type: "step", id: "london", text: "Searching hotels in London", status: "active" }) },
  { delay: 1100, run: () => SearchProgressStore.apply({ type: "step", id: "paris", text: "Searching hotels in Paris", status: "active" }) },
  { delay: 1900, run: () => SearchProgressStore.apply({ type: "message", text: "Comparing 18 options…", stepId: "london" }) },
  { delay: 2300, run: () => hotelCard("london", 0, LONDON[0]) },
  { delay: 2900, run: () => hotelCard("paris", 0, PARIS[0]) },
  { delay: 3300, run: () => hotelCard("london", 1, LONDON[1]) },
  // London finishes here while Paris keeps spinning — the key thing to watch.
  { delay: 3900, run: () => SearchProgressStore.apply({ type: "step", id: "london", status: "done" }) },
  { delay: 4200, run: () => SearchProgressStore.apply({ type: "message", text: "Checking availability…", stepId: "paris" }) },
  { delay: 4700, run: () => hotelCard("paris", 1, PARIS[1]) },
  { delay: 5400, run: () => SearchProgressStore.apply({ type: "step", id: "paris", status: "done" }) },
  { delay: 5900, run: () => SearchProgressStore.apply({ type: "message", text: "Putting your options together…" }) },
  { delay: 6600, run: () => SearchProgressStore.apply({ type: "end" }) },
];

let timers: ReturnType<typeof setTimeout>[] = [];
const clearTimers = (): void => {
  timers.forEach((t) => clearTimeout(t));
  timers = [];
};

export const searchProgressDevHelper = {
  // Auto-fire the demo when a chat loading bubble appears (dev convenience).
  // Turn off with: window.__searchProgress.auto = false
  auto: true,
  start: (header?: string) => SearchProgressStore.apply({ type: "start", header }),
  message: (text: string, stepId?: string) =>
    SearchProgressStore.apply({ type: "message", text, stepId }),
  card: (kind: CardKind, data: Record<string, unknown>, stepId?: string) =>
    SearchProgressStore.apply({ type: "card", card: { kind, data }, stepId }),
  step: (id: string, status: "active" | "done", text?: string) =>
    SearchProgressStore.apply({ type: "step", id, status, text }),
  end: () => SearchProgressStore.apply({ type: "end" }),
  reset: (): void => {
    clearTimers();
    SearchProgressStore.reset();
  },
  demo: (): void => {
    clearTimers();
    SearchProgressStore.reset();
    SCRIPT.forEach((step) => timers.push(setTimeout(step.run, step.delay)));
  },
  // Concurrent multi-city demo — two spinners that resolve independently.
  multiCity: (): void => {
    clearTimers();
    SearchProgressStore.reset();
    MULTI_SCRIPT.forEach((s) => timers.push(setTimeout(s.run, s.delay)));
  },
};
